import {
  AdminAuthTokens,
  AdminLoginRequest,
  AdminLoginResult,
  AuthTokens,
  ChangePasswordInput,
  LoginChallenge,
  LoginRequest,
  LoginResult,
  PasswordCompleteRequest,
  PasswordStartRequest,
  RefreshHeaders,
  RefreshTokens,
  ResendLogin2faRequest,
  VerifyLogin2faRequest,
} from '../interfaces/auth';
import { empty, json, problem, type PathDef } from '../../util/pathDef';

export const authLogin: PathDef = {
  method: 'post',
  path: '/auth/login',
  handler: 'domains/bff/src/auth/login/index.handler',
  tags: ['auth'],
  summary: 'Start login (password); MFA challenge or session tokens',
  description:
    'Authenticates via the HedgeFacts Legacy API using the datasource from the login body. When a valid `mfaTrustToken` (≤2h since last activity; slides on refresh/login) is supplied for the same user/datasource, returns access/refresh/trust JWTs directly. Otherwise creates a short-lived MFA challenge and emails a one-time code (5 minute TTL, single use) — call `/auth/2fa/verify` to finish. Access TTL 14 minutes; refresh TTL 1 hour; MFA trust TTL 2 hours of inactivity.',
  security: 'public',
  request: LoginRequest,
  responses: {
    200: json('MFA challenge created, or session tokens when MFA trust is still valid', LoginResult),
    400: problem('Invalid request'),
    401: problem('Invalid credentials'),
    502: problem('HedgeFacts API unavailable'),
  },
};

export const authVerify2fa: PathDef = {
  method: 'post',
  path: '/auth/2fa/verify',
  handler: 'domains/bff/src/auth/2fa/verify/index.handler',
  tags: ['auth'],
  summary: 'Complete login with emailed one-time code',
  description:
    'Exchanges a valid MFA challenge token plus the emailed 6-digit code for access and refresh JWTs. Wrong, expired, or already-used codes are rejected with a generic unauthorized response.',
  security: 'public',
  request: VerifyLogin2faRequest,
  responses: {
    200: json('Authenticated', AuthTokens),
    400: problem('Invalid request'),
    401: problem('Invalid or expired code'),
    502: problem('Auth service unavailable'),
  },
};

export const authResend2fa: PathDef = {
  method: 'post',
  path: '/auth/2fa/resend',
  handler: 'domains/bff/src/auth/2fa/resend/index.handler',
  tags: ['auth'],
  summary: 'Resend login one-time code',
  description:
    'Issues a new single-use code for an active MFA challenge and emails it. Rate-limited per email. Returns an updated challenge payload (same challenge token family; expiresIn refreshed).',
  security: 'public',
  request: ResendLogin2faRequest,
  responses: {
    200: json('New code emailed', LoginChallenge),
    400: problem('Invalid request'),
    401: problem('Invalid or expired challenge'),
    429: problem('Too many code requests'),
    502: problem('Auth service unavailable'),
  },
};

export const authRefresh: PathDef = {
  method: 'post',
  path: '/auth/refresh',
  handler: 'domains/bff/src/auth/refresh/index.handler',
  tags: ['auth'],
  summary: 'Refresh access token',
  description:
    'Authorization holds the refresh JWT. X-Expired-Token holds the expired access JWT; both signatures are verified and the matching web or Admin Center claim shape is reissued (expired access `exp` is ignored). Web sessions touch the legacy session unless internally sourced; Admin Central sessions do not call touchSession. An Admin Center `X-Mfa-Trust` header slides that app’s 2h skip-2FA window when it matches the user. Resets TTLs (access 14 minutes, refresh 1 hour).',
  security: 'public',
  headers: RefreshHeaders,
  responses: {
    200: json('New tokens', RefreshTokens),
    400: problem('Invalid request'),
    401: problem('Invalid or expired refresh token'),
    502: problem('HedgeFacts API unavailable'),
  },
};

export const authChangePassword: PathDef = {
  method: 'post',
  path: '/auth/change-password',
  handler: 'domains/bff/src/auth/changePassword/index.handler',
  tags: ['auth'],
  summary: 'Change password',
  description: 'Partner Cloud users only. SOAP / desktop users change passwords in the Desktop app. Never log passwords.',
  security: 'bearerAuth',
  request: ChangePasswordInput,
  responses: {
    204: empty('Password changed'),
    400: problem('Invalid request'),
    401: problem('Unauthorized or current password incorrect'),
    404: problem('Not found'),
    502: problem('Auth service unavailable'),
  },
};

export const authPasswordStart: PathDef = {
  method: 'post',
  path: '/auth/password/start',
  handler: 'domains/bff/src/auth/password/start/index.handler',
  tags: ['auth'],
  summary: 'Start first-login or forgot-password setup',
  description:
    'Public. Emails a one-time code when an active Cloud account exists. SOAP / HedgeFacts desktop users have no Cloud row and receive 404.',
  security: 'public',
  request: PasswordStartRequest,
  responses: {
    200: json('Challenge created', LoginChallenge),
    400: problem('Invalid request'),
    404: problem('No Cloud account for this email'),
    429: problem('Too many code requests'),
    502: problem('Auth service unavailable'),
  },
};

export const authPasswordResend: PathDef = {
  method: 'post',
  path: '/auth/password/resend',
  handler: 'domains/bff/src/auth/password/resend/index.handler',
  tags: ['auth'],
  summary: 'Resend first-login or forgot-password code',
  description: 'Issues or re-emails a one-time code for an active password-setup challenge. Rate-limited per email.',
  security: 'public',
  request: ResendLogin2faRequest,
  responses: {
    200: json('New code emailed', LoginChallenge),
    400: problem('Invalid request'),
    401: problem('Invalid or expired challenge'),
    429: problem('Too many code requests'),
    502: problem('Auth service unavailable'),
  },
};

export const authPasswordVerify: PathDef = {
  method: 'post',
  path: '/auth/password/verify',
  handler: 'domains/bff/src/auth/password/verify/index.handler',
  tags: ['auth'],
  summary: 'Verify first-login or forgot-password code',
  description:
    'Public. Checks the emailed 6-digit code and challenge token before the password form. Does not consume the code — `/auth/password/complete` does that when the new password is stored.',
  security: 'public',
  request: VerifyLogin2faRequest,
  responses: {
    204: empty('Code is valid'),
    400: problem('Invalid request'),
    401: problem('Invalid or expired code'),
    502: problem('Auth service unavailable'),
  },
};

export const authPasswordComplete: PathDef = {
  method: 'post',
  path: '/auth/password/complete',
  handler: 'domains/bff/src/auth/password/complete/index.handler',
  tags: ['auth'],
  summary: 'Verify emailed code, set a password, and start a session',
  description:
    'Public first-login and forgot-password finish. Verifies the emailed 6-digit code, stores the new password, and returns session tokens without a second MFA round trip.',
  security: 'public',
  request: PasswordCompleteRequest,
  responses: {
    200: json('Authenticated', AuthTokens),
    400: problem('Invalid request'),
    401: problem('Invalid or expired code'),
    502: problem('Auth service unavailable'),
  },
};

export const adminLogin: PathDef = {
  method: 'post',
  path: '/admin/login',
  handler: 'domains/bff/src/admin/login/index.handler',
  tags: ['admin'],
  summary: 'Admin Center login (username and password)',
  description:
    'Authenticates an Admin Central user with username and password. When a valid `mfaTrustToken` (≤2h since last activity; slides on refresh/login) is supplied for the same admin user, returns access and refresh JWTs directly. Otherwise emails a one-time code to the market-library address on that admin user — call `/admin/2fa/verify` to finish. The session JWT claims are firstName, lastName, ticket, sub, isAdminCentral (literal true), iat, and exp. Email is not a session claim.',
  security: 'public',
  request: AdminLoginRequest,
  responses: {
    200: json('Code emailed, or session tokens when MFA trust is still valid', AdminLoginResult),
    400: problem('Invalid request'),
    401: problem('Invalid credentials'),
    429: problem('Too many code requests'),
    502: problem('Admin Central unavailable'),
  },
};

export const adminVerify2fa: PathDef = {
  method: 'post',
  path: '/admin/2fa/verify',
  handler: 'domains/bff/src/admin/2fa/verify/index.handler',
  tags: ['admin'],
  summary: 'Complete Admin Center login with the emailed code',
  description:
    'Exchanges the Admin Center MFA challenge and the emailed 6-digit code for an Admin Center access and refresh JWT, plus an MFA trust JWT that skips email 2FA for 2 hours of inactivity.',
  security: 'public',
  request: VerifyLogin2faRequest,
  responses: {
    200: json('Authenticated', AdminAuthTokens),
    400: problem('Invalid request'),
    401: problem('Invalid or expired code'),
    502: problem('Auth service unavailable'),
  },
};

export const adminResend2fa: PathDef = {
  method: 'post',
  path: '/admin/2fa/resend',
  handler: 'domains/bff/src/admin/2fa/resend/index.handler',
  tags: ['admin'],
  summary: 'Resend the Admin Center one-time code',
  description: 'Emails a new single-use code for an active Admin Center MFA challenge.',
  security: 'public',
  request: ResendLogin2faRequest,
  responses: {
    200: json('New code emailed', LoginChallenge),
    400: problem('Invalid request'),
    401: problem('Invalid or expired challenge'),
    429: problem('Too many code requests'),
    502: problem('Auth service unavailable'),
  },
};

export const authPaths: PathDef[] = [
  authLogin,
  authVerify2fa,
  authResend2fa,
  adminLogin,
  adminVerify2fa,
  adminResend2fa,
  authRefresh,
  authChangePassword,
  authPasswordStart,
  authPasswordResend,
  authPasswordVerify,
  authPasswordComplete,
];
